/**
 * AnalysisProgress
 * Real-time progress display for codebase analysis
 */

import React, { useEffect, useState } from 'react';
import { CheckCircle, Circle, Loader, AlertCircle } from 'lucide-react';
import { developerAgentApi } from '../../../services/api';
import type { AnalysisStatus } from '../../../types';
import styles from './AnalysisProgress.module.css';

export interface AnalysisProgressProps {
  analysisId: string;
  repoName: string;
  onComplete?: (analysisId: string) => void;
  onError?: (error: string) => void;
}

export const AnalysisProgress: React.FC<AnalysisProgressProps> = ({
  analysisId,
  repoName,
  onComplete,
  onError,
}) => {
  const [status, setStatus] = useState<AnalysisStatus | null>(null);
  const [connectionMode, setConnectionMode] = useState<'sse' | 'polling'>('sse');

  useEffect(() => {
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let eventSource: EventSource | null = null;
    let isCancelled = false;

    // Polling fallback: fetch analysis status via REST API
    const startPolling = () => {
      setConnectionMode('polling');
      pollTimer = setInterval(async () => {
        if (isCancelled) return;
        try {
          const response = await developerAgentApi.getAnalysisStatus(analysisId);
          if (!isCancelled && response.success && response.data) {
            const data = response.data;
            setStatus(data);

            if (data.status === 'complete') {
              if (pollTimer) clearInterval(pollTimer);
              onComplete?.(analysisId);
            } else if (data.status === 'failed') {
              if (pollTimer) clearInterval(pollTimer);
              onError?.(data.error || 'Analysis failed');
            }
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }, 3000); // Poll every 3 seconds
    };

    // Try SSE first
    try {
      eventSource = developerAgentApi.subscribeToAnalysisProgress(analysisId);

      eventSource.onmessage = (event) => {
        if (isCancelled) return;
        try {
          const data = JSON.parse(event.data) as AnalysisStatus;
          setStatus(data);

          if (data.status === 'complete') {
            eventSource?.close();
            onComplete?.(analysisId);
          } else if (data.status === 'failed') {
            eventSource?.close();
            onError?.(data.error || 'Analysis failed');
          }
        } catch (err) {
          console.error('Failed to parse SSE data:', err);
        }
      };

      eventSource.onerror = () => {
        console.warn('SSE connection failed, falling back to polling');
        eventSource?.close();
        eventSource = null;
        if (!isCancelled) {
          startPolling();
        }
      };
    } catch {
      // SSE not available, use polling directly
      startPolling();
    }

    // Cleanup
    return () => {
      isCancelled = true;
      eventSource?.close();
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [analysisId, onComplete, onError]);

  if (!status) {
    return (
      <div className={styles.progress}>
        <div className={styles.loading}>
          <Loader className={styles.spinning} size={24} />
          <span>Connecting...</span>
        </div>
      </div>
    );
  }

  // Get icon for stage status
  const getStageIcon = (stageStatus: string) => {
    switch (stageStatus) {
      case 'complete':
        return <CheckCircle size={18} className={styles.iconComplete} />;
      case 'running':
        return <Loader size={18} className={`${styles.iconRunning} ${styles.spinning}`} />;
      case 'failed':
        return <AlertCircle size={18} className={styles.iconFailed} />;
      default:
        return <Circle size={18} className={styles.iconPending} />;
    }
  };

  return (
    <div className={styles.progress}>
      {/* Header */}
      <div className={styles.header}>
        <h3 className={styles.title}>Analyzing {repoName}</h3>
        <span className={styles.currentStage}>{status.currentStage}</span>
      </div>

      {/* Progress Bar */}
      <div className={styles.progressBar}>
        <div
          className={styles.progressFill}
          style={{ width: `${status.progress}%` }}
        />
      </div>
      <div className={styles.progressText}>{status.progress}%</div>

      {/* Stages */}
      <div className={styles.stages}>
        {status.stages.map((stage, index) => (
          <div
            key={index}
            className={`${styles.stage} ${styles[`stage-${stage.status}`]}`}
          >
            <div className={styles.stageIcon}>{getStageIcon(stage.status)}</div>
            <div className={styles.stageName}>{stage.name}</div>
          </div>
        ))}
      </div>

      {/* Error Message */}
      {status.status === 'failed' && status.error && (
        <div className={styles.error}>
          <AlertCircle size={16} />
          <span>{status.error}</span>
        </div>
      )}
    </div>
  );
};

export default AnalysisProgress;
